import apiClient from './client';

export enum CageType {
  WALK_IN = 'WALK_IN',
  SUSPENDED = 'SUSPENDED',
  FLIGHT = 'FLIGHT',
  BREEDING = 'BREEDING',
  ISOLATION = 'ISOLATION',
  HOSPITAL = 'HOSPITAL',
  NURSERY = 'NURSERY',
  HOLDING = 'HOLDING',
  AVIARY = 'AVIARY',
  OTHER = 'OTHER',
}

export enum CageStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  MAINTENANCE = 'MAINTENANCE',
  RETIRED = 'RETIRED',
}

export enum FacingDirection {
  N = 'N',
  NE = 'NE',
  E = 'E',
  SE = 'SE',
  S = 'S',
  SW = 'SW',
  W = 'W',
  NW = 'NW',
  INDOOR = 'INDOOR',
}

export type DimensionUnit = 'mm' | 'cm' | 'm' | 'inch';

export interface NestBox {
  id: string;
  label: string;
  type?: string;
  position?: string;
  notes?: string;
}

export interface Cage {
  id: string;
  cageNumber: string;
  name?: string | null;
  cageType: CageType;
  status: CageStatus;
  wireType?: string | null;
  facingDirection?: FacingDirection | null;
  location?: string | null;
  dimensionUnit: DimensionUnit;
  length?: number | null;
  height?: number | null;
  breadth?: number | null;
  cubicVolume?: number | null;
  nestBoxes: NestBox[];
  floorType?: string | null;
  hasCover: boolean;
  isOutdoor: boolean;
  notes?: string | null;
  occupancy: number;
  nestBoxCount: number;
  volumeLabel: string;
  createdAt: string;
  updatedAt: string;
}

export interface CageOption {
  id: string;
  cageNumber: string;
  cageType: CageType;
  location: string | null;
  status: CageStatus;
  nestBoxCount: number;
}

export interface CreateCageDto {
  cageNumber: string;
  name?: string;
  cageType?: CageType;
  status?: CageStatus;
  wireType?: string;
  facingDirection?: FacingDirection;
  location?: string;
  dimensionUnit?: DimensionUnit;
  length?: number;
  height?: number;
  breadth?: number;
  nestBoxes?: NestBox[];
  floorType?: string;
  hasCover?: boolean;
  isOutdoor?: boolean;
  notes?: string;
}

export type UpdateCageDto = Partial<CreateCageDto>;

export interface CageFilters {
  search?: string;
  cageType?: CageType;
  status?: CageStatus;
  location?: string;
  wireType?: string;
  facingDirection?: string;
  page?: number;
  limit?: number;
}

export interface CageListResponse {
  cages: Cage[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CageStats {
  total: number;
  active: number;
  maintenance: number;
  occupied: number;
  empty: number;
}

export const CAGE_TYPE_LABELS: Record<CageType, string> = {
  [CageType.WALK_IN]: 'Walk-in',
  [CageType.SUSPENDED]: 'Suspended',
  [CageType.FLIGHT]: 'Flight',
  [CageType.BREEDING]: 'Breeding',
  [CageType.ISOLATION]: 'Isolation',
  [CageType.HOSPITAL]: 'Hospital',
  [CageType.NURSERY]: 'Nursery',
  [CageType.HOLDING]: 'Holding',
  [CageType.AVIARY]: 'Aviary',
  [CageType.OTHER]: 'Other',
};

export const CAGE_STATUS_LABELS: Record<CageStatus, string> = {
  [CageStatus.ACTIVE]: 'Active',
  [CageStatus.INACTIVE]: 'Inactive',
  [CageStatus.MAINTENANCE]: 'Maintenance',
  [CageStatus.RETIRED]: 'Retired',
};

export const FACING_LABELS: Record<FacingDirection, string> = {
  [FacingDirection.N]: 'North',
  [FacingDirection.NE]: 'North-east',
  [FacingDirection.E]: 'East',
  [FacingDirection.SE]: 'South-east',
  [FacingDirection.S]: 'South',
  [FacingDirection.SW]: 'South-west',
  [FacingDirection.W]: 'West',
  [FacingDirection.NW]: 'North-west',
  [FacingDirection.INDOOR]: 'Indoor',
};

export const WIRE_TYPE_PRESETS = [
  '13mm × 25mm welded mesh',
  '12mm × 12mm welded mesh',
  '25mm × 25mm welded mesh',
  '13mm hexagonal mesh',
  '19mm hexagonal mesh',
  '25mm hexagonal mesh',
  'Aviary mesh 1.4mm',
  'Solid panel / mixed',
];

const TO_CM: Record<DimensionUnit, number> = {
  mm: 0.1,
  cm: 1,
  m: 100,
  inch: 2.54,
};

export function cubicVolumeCm3(
  length: number,
  height: number,
  breadth: number,
  unit: DimensionUnit = 'cm',
): number {
  const l = Number(length) || 0;
  const h = Number(height) || 0;
  const b = Number(breadth) || 0;
  if (l <= 0 || h <= 0 || b <= 0) return 0;
  const factor = TO_CM[unit] ?? 1;
  return Math.round(l * factor * h * factor * b * factor * 100) / 100;
}

export function formatVolume(cm3: number): string {
  if (!cm3 || cm3 <= 0) return '—';
  if (cm3 >= 1_000_000) return `${(cm3 / 1_000_000).toFixed(2)} m³`;
  if (cm3 >= 1000) return `${(cm3 / 1000).toFixed(1)} L`;
  return `${cm3.toFixed(0)} cm³`;
}

export function cageStatusColor(status: CageStatus): string {
  switch (status) {
    case CageStatus.ACTIVE:
      return 'bg-emerald-100 text-emerald-800';
    case CageStatus.MAINTENANCE:
      return 'bg-amber-100 text-amber-800';
    case CageStatus.INACTIVE:
      return 'bg-slate-100 text-slate-700';
    case CageStatus.RETIRED:
      return 'bg-violet-100 text-violet-800';
    default:
      return 'bg-slate-100 text-slate-700';
  }
}

function toParams(filters: CageFilters = {}): Record<string, string> {
  const params: Record<string, string> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = String(value);
    }
  });
  return params;
}

export const cagesApi = {
  list: async (filters: CageFilters = {}): Promise<CageListResponse> => {
    const response = await apiClient.get<CageListResponse>('/cages', toParams(filters));
    return response.data;
  },

  options: async (includeInactive = false): Promise<CageOption[]> => {
    const response = await apiClient.get<CageOption[]>(
      '/cages/options',
      includeInactive ? { includeInactive: 'true' } : undefined,
    );
    return response.data;
  },

  stats: async (): Promise<CageStats> => {
    const response = await apiClient.get<CageStats>('/cages/stats');
    return response.data;
  },

  getById: async (id: string): Promise<Cage> => {
    const response = await apiClient.get<Cage>(`/cages/${id}`);
    return response.data;
  },

  create: async (data: CreateCageDto): Promise<Cage> => {
    const response = await apiClient.post<Cage>('/cages', data);
    return response.data;
  },

  update: async (id: string, data: UpdateCageDto): Promise<Cage> => {
    const response = await apiClient.patch<Cage>(`/cages/${id}`, data);
    return response.data;
  },

  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/cages/${id}`);
  },
};
